import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Separator } from '@/components/ui/separator';
import { CheckCircle, XCircle, Loader2, ExternalLink, AlertTriangle, TrendingUp, TrendingDown, Clock } from 'lucide-react';
import { RebalancingProgress } from '@/hooks/use-rebalancing-executor';

interface RebalancingProgressModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCancel: () => void;
  progress: RebalancingProgress;
  isExecuting: boolean;
  isCancelling: boolean;
}

export default function RebalancingProgressModal({
  isOpen,
  onClose,
  onCancel,
  progress,
  isExecuting,
  isCancelling,
}: RebalancingProgressModalProps) {
  const progressPercentage = progress.total > 0 ? (progress.completed / progress.total) * 100 : 0;

  const completedTrades = progress.trades.filter(t => t.status === 'completed');
  const failedTrades = progress.trades.filter(t => t.status === 'failed');
  const pendingTrades = progress.trades.filter(t => t.status === 'pending');

  const isComplete = progress.completed + progress.failed === progress.total && progress.total > 0;

  // Estimate remaining time based on progress (assuming ~5 seconds per trade)
  const estimatedTimeRemaining = isExecuting && !isComplete
    ? Math.max(5, (progress.total - progress.completed - progress.failed) * 5)
    : null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="w-full max-w-2xl max-h-[80vh] overflow-y-auto mx-4 sm:mx-auto" aria-modal="true" role="dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Loader2 className={`w-5 h-5 ${isExecuting ? 'animate-spin' : ''}`} />
            Rebalancing Progress
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {/* Progress Bar */}
          <div>
            <div className="flex justify-between text-sm mb-2">
              <span>Progress</span>
              <span>{progress.completed + progress.failed} / {progress.total}</span>
            </div>
            <Progress value={progressPercentage} className="w-full" />
            {estimatedTimeRemaining !== null && (
              <div className="flex items-center justify-center gap-1 mt-2 text-xs text-muted-foreground">
                <Clock className="w-3 h-3" />
                <span>~{estimatedTimeRemaining}s remaining</span>
              </div>
            )}
          </div>

          {/* Current Trade */}
          {progress.current && isExecuting && (
            <Alert>
              <Loader2 className="w-4 h-4 animate-spin" />
              <AlertDescription>
                Currently executing: {progress.current}
              </AlertDescription>
            </Alert>
          )}

          {/* Trade Status Summary */}
          <div className="grid grid-cols-3 gap-4">
            <div className="text-center p-3 bg-success/10 rounded-lg">
              <CheckCircle className="w-6 h-6 text-success mx-auto mb-2" />
              <div className="text-2xl font-bold text-success">{progress.completed}</div>
              <div className="text-sm text-muted-foreground">Completed</div>
            </div>
            <div className="text-center p-3 bg-destructive/10 rounded-lg">
              <XCircle className="w-6 h-6 text-destructive mx-auto mb-2" />
              <div className="text-2xl font-bold text-destructive">{progress.failed}</div>
              <div className="text-sm text-muted-foreground">Failed</div>
            </div>
            <div className="text-center p-3 bg-muted rounded-lg">
              <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" />
              <div className="text-2xl font-bold">{pendingTrades.length}</div>
              <div className="text-sm text-muted-foreground">Pending</div>
            </div>
          </div>

          {/* Trade Details */}
          <div>
            <h4 className="font-medium mb-3">Trade Details</h4>
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {progress.trades.map((trade, index) => (
                <div key={index} className="flex items-center justify-between p-3 bg-muted rounded-lg">
                  <div className="flex items-center gap-3">
                    {trade.status === 'completed' ? (
                      <CheckCircle className="w-4 h-4 text-success" />
                    ) : trade.status === 'failed' ? (
                      <XCircle className="w-4 h-4 text-destructive" />
                    ) : (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    )}
                    <div>
                      <div className="flex items-center gap-2">
                        {trade.recommendation.action === 'Buy' ? (
                          <TrendingUp className="w-4 h-4 text-success" />
                        ) : (
                          <TrendingDown className="w-4 h-4 text-destructive" />
                        )}
                        <span className="font-medium">{trade.recommendation.token}</span>
                        <Badge variant={trade.recommendation.action === 'Buy' ? 'default' : 'destructive'} className="text-xs">
                          {trade.recommendation.action}
                        </Badge>
                      </div>
                      <div className="text-sm text-muted-foreground">
                        {trade.recommendation.amount.toFixed(4)} {trade.recommendation.action === 'Buy' ? 'SOL' : 'tokens'}
                      </div>
                    </div>
                  </div>
                  <div className="text-right">
                    {trade.status === 'completed' && trade.txSignature && (
                      <a
                        href={`https://explorer.solana.com/tx/${trade.txSignature}?cluster=devnet`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-primary hover:underline flex items-center gap-1 text-sm"
                      >
                        <ExternalLink className="w-3 h-3" />
                        View TX
                      </a>
                    )}
                    {trade.status === 'failed' && trade.error && (
                      <div className="text-xs text-destructive max-w-32 truncate" title={trade.error}>
                        {trade.error}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Final Summary */}
          {isComplete && (
            <Alert className={progress.failed === 0 ? 'border-success' : 'border-warning'}>
              {progress.failed === 0 ? (
                <CheckCircle className="h-4 w-4 text-success" />
              ) : (
                <AlertTriangle className="h-4 w-4 text-warning" />
              )}
              <AlertDescription>
                {progress.failed === 0
                  ? `All ${progress.completed} trades completed successfully!`
                  : `${progress.completed} trades completed, ${progress.failed} failed. Check the details above for failed transactions.`
                }
              </AlertDescription>
            </Alert>
          )}
        </div>

        <DialogFooter>
          {isExecuting && !isCancelling && (
            <Button variant="destructive" onClick={onCancel} disabled={isCancelling}>
              {isCancelling ? 'Cancelling...' : 'Cancel Remaining'}
            </Button>
          )}
          <Button onClick={onClose} disabled={isExecuting && !isComplete}>
            {isComplete ? 'Close' : 'Hide Progress'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}